import React, { useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { Link } from '@this/shared/ui/navigations/link';
import { Table, TableBody, TableRow, TableCell } from '@this/shared/ui/data_displays/table';
import CancelPolicySection from './cancel_policy_section';

interface Props {
  tripId: number;
  serviceTel: string;
  isPremiumSupport: boolean;
  mode: 'bulk' | 'individual';
}

const CancelPolicyContent: React.FC<Props> = ({ tripId, serviceTel, isPremiumSupport, mode }) => {
  const isMobile = useMediaQuery('(max-width: 768px)', { noSsr: true });
  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  const intraScopeNotice =
    mode === 'bulk'
      ? '旅程の一部をキャンセルしたい場合、旅程詳細画面より個別キャンセル依頼をご利用ください。'
      : '旅程全体をキャンセルしたい場合、旅程詳細画面より一括キャンセル依頼をご利用ください。';

  const businessHours = isPremiumSupport ? '9:00〜18:00' : '9:00〜18:00（平日）';

  return (
    <div>
      <CancelPolicySection title="キャンセル依頼について">
        <ul style={styles.list}>
          <li>
            この画面で行うキャンセル依頼は、株式会社トランスファーデータに対する「要望」として行われるものであり、ご利用の宿泊施設・交通機関等（以下、「サプライヤー」といいます。）へのキャンセル依頼ではありませんので、あらかじめご了承ください。
          </li>
          <li>
            また、サプライヤーのキャンセルポリシーによっては、必ずしもご返金をお約束できるものではございませんので、重ねてご了承ください。
          </li>
          <li>なお、{intraScopeNotice}</li>
        </ul>
      </CancelPolicySection>

      <CancelPolicySection title="ご利用当日のキャンセル依頼">
        <ul style={styles.list}>
          <li>
            当日のキャンセルは、ご自身でご対応をお願いいたします（キャンセル方法は
            <Link isExternal href="https://aitravel.jp/knowledges/000001075">
              こちら
            </Link>
            をご確認ください）。
          </li>
          <li>
            ご自身でキャンセルした場合は、弊社にてご請求データを更新する必要がございます。キャンセルがお済みになりましたら、その旨チャットよりお知らせください。
          </li>
        </ul>
      </CancelPolicySection>

      <CancelPolicySection title="キャンセル料金について">
        <ul style={styles.list}>
          <li>
            お客様の本画面キャンセル依頼を受け、当社は、サプライヤーのキャンセルポリシーに則ってキャンセル依頼を行います。
          </li>
          <li>
            サプライヤーやキャンセルポリシーによっては、サプライヤーの違約金・キャンセル料金が発生する場合がございます。
          </li>
        </ul>
      </CancelPolicySection>

      <CancelPolicySection title="注意事項">
        <ul style={styles.list}>
          <li>キャンセルご依頼後の、キャンセル依頼の取り消しはできかねます。</li>
          <li>弊社の手配手数料等事務手数料は、返金対象外でございます。</li>
        </ul>
      </CancelPolicySection>

      <CancelPolicySection title="お問い合わせ">
        <p style={styles.contactIntro}>質問、不明点がある場合は下記までお問い合わせください。</p>
        <Table style={styles.table}>
          <TableBody style={styles.body}>
            <TableRow style={styles.row}>
              <TableCell th style={styles.contactLabelCell}>
                チャット
              </TableCell>
              <TableCell style={styles.contactValueCell}>
                <Link isExternal href={`/trips/${tripId}/messages`}>
                  チャットを開く
                </Link>
              </TableCell>
            </TableRow>
            <TableRow style={styles.row}>
              <TableCell th style={styles.contactLabelCell}>
                電話窓口
              </TableCell>
              <TableCell style={styles.contactValueCell}>
                <div>TEL：{serviceTel}</div>
                <div>＜営業時間＞</div>
                <div>{businessHours}</div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CancelPolicySection>
    </div>
  );
};

const getStyles = (isMobile: boolean) => {
  const cell: React.CSSProperties = {
    border: '1px solid #d3d7db',
    padding: '8px 12px',
    verticalAlign: 'top',
    fontSize: 13,
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit',
    boxSizing: 'border-box',
    ...(isMobile ? { display: 'block', width: '100%' } : {})
  };
  return {
    list: {
      listStyle: 'disc' as const,
      paddingLeft: 20,
      margin: 0
    },
    contactIntro: {
      marginBottom: 12,
      fontSize: 13
    },
    table: {
      borderCollapse: 'collapse' as const,
      width: '100%',
      fontSize: 13,
      ...(isMobile ? { display: 'block' as const } : {})
    },
    body: isMobile ? { display: 'block' as const } : undefined,
    row: isMobile ? { display: 'block' as const } : undefined,
    contactLabelCell: {
      ...cell,
      backgroundColor: '#f4f5f6',
      fontWeight: 'bold' as const,
      ...(isMobile ? {} : { width: '30%' })
    },
    contactValueCell: {
      ...cell,
      backgroundColor: 'transparent'
    }
  };
};

export default CancelPolicyContent;
